/**
 * storageWatchdog — says when an IndexedDB store is not opening.
 *
 * A localforage store opens its database on first use. If another tab of the
 * same origin holds that database open across a version change, the open
 * waits — forever, silently. An app's first read (a token cache hydrating)
 * then never resolves, and it never reaches its group: "Disconnected", no
 * error anywhere. Seen 2026-10-05 on CameraMap; twenty minutes of looking at
 * servers for a tab that needed closing.
 *
 * watchStore(instance, name) races the store's open against a timer. Past it,
 * a console warning names the likely cause, and a 'blocked' event fires for
 * an app's status line; 'opened' follows when the open completes (with how
 * long it took), so the status can clear itself.
 *
 *   acequia.storage.watch (EventTarget): 'blocked' { name, ms }, 'opened' { name, ms, late }
 *   acequia.storage.blocked(): the names still waiting
 *   acequia.storage.message(): the sentence to show, or null
 */

const DEFAULT_TIMEOUT = 5000

export const watch = new EventTarget()
const waiting = new Map() // name → started at

export function blocked() {
    return [...waiting.keys()]
}

/** What to tell the user while a store is blocked, or null. */
export function message() {
    if (!waiting.size) return null
    const origin = typeof location !== 'undefined' ? location.origin : 'this site'
    return `Storage is waiting on another tab — close other ${origin.replace(/^https?:\/\//, '')} tabs and reload`
}

/**
 * Watch a localforage instance's open. Returns the instance, so it wraps the
 * createInstance call in place. Outside a browser, or on a store with no
 * ready(), it does nothing.
 */
export function watchStore(instance, name, { timeoutMs = DEFAULT_TIMEOUT } = {}) {
    if (!instance || typeof instance.ready !== 'function') return instance
    const started = Date.now()
    let timer = setTimeout(() => {
        timer = null
        waiting.set(name, started)
        const origin = typeof location !== 'undefined' ? location.origin : 'this origin'
        console.warn(`[acequia] storage "${name}" has not opened after ${Math.round(timeoutMs / 1000)} s — another tab may be holding the database open across an upgrade; close other ${origin} tabs and reload`)
        watch.dispatchEvent(new CustomEvent('blocked', { detail: { name, ms: timeoutMs } }))
    }, timeoutMs)
    Promise.resolve()
        .then(() => instance.ready())
        .then(() => {
            const ms = Date.now() - started
            const late = timer === null
            if (timer) clearTimeout(timer)
            if (late) {
                waiting.delete(name)
                console.warn(`[acequia] storage "${name}" opened after ${(ms / 1000).toFixed(1)} s`)
            }
            watch.dispatchEvent(new CustomEvent('opened', { detail: { name, ms, late } }))
        })
        .catch(() => { if (timer) clearTimeout(timer) })
    return instance
}
